import classNames from 'classnames';
import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Chooser, { ChooserGroup } from '../Chooser';
import * as events from '../../lib/analytics/events';
import * as types from '../../types';
import SubscriptionOptionButton, { SubscriptionOption } from '../SubscriptionOptionDetails';
import styles from './SubscriptionOptions.module.scss';
import { showNanoVariant } from '../NanoBanner/NanoBanner';
import { showRescuePdpRedesign } from '../../views/RescueLanding/RescueBanner';
import { getFiGrowthBook, useIsGrowthBookEnabled } from '../../lib/growthbook';
import OllieWelcomeBoxImage from '../../assets/images/ollie-welcome-box.png';
import NanoValueProps from '../../views/ProductDetails/Series3CollarDetails/components/NanoValueProps/NanoValueProps';
import { monthlySubscriptionIsSupportedInCountry, useLocale } from '../../contexts/LocaleContext';
import { formattedCentsToDollars, getLocalizedPricing } from '../../lib/util';
import { useIsGift } from '../../lib/util/isGift';
import useFreeTrialEligibility from '../../hooks/useFreeTrialEligibility';
import useFreeTrialEnabled from '../../hooks/useFreeTrialEnabled';
import { useActivePromoCode } from '../../hooks/useActivePromoCode';
import { usePromoCodeDetails } from '../../hooks/usePromoCodeDetails';
import useIsReferralCheckoutV2 from '../../hooks/useIsReferralCheckoutV2';
import WhatsIncluded from '../SubscriptionOptionDetails/WhatsIncluded/WhatsIncluded';

export interface CouponPillDisplay {
  shouldShow: boolean;
  title?: string;
  subtitle?: string;
}

interface ChooserGroupsProps {
  allowNoSubscription?: boolean;
  subscriptionProducts: SubscriptionOption[];
  showReferralPill?: boolean;
  couponPillDisplay?: CouponPillDisplay;
  showUpsellsInChooser?: boolean;
  monthlyUpgrade?: boolean;
  showSubscriptionLengthDiscount?: boolean;
  prominentPricingDesign?: boolean;
  disableSavingsDisplay?: boolean;
  redemptionType?: types.RedemptionType;
  hasValidRedemptionCode?: boolean;
  redemptionDiscountInCents?: number | null;
  productCategory?: types.ProductCategory;
  onStartFreeTrial?: (subscriptionSku: string) => void;
  freeTrialDisabled?: boolean;
  hideMonthlyOption?: boolean;
}

function giftOverrideSubtitle(subscriptionProduct: SubscriptionOption) {
  if (subscriptionProduct.renewalMonths % 12 === 0) {
    const years = Math.round(subscriptionProduct.renewalMonths / 12);
    return `Fi Mini + ${years} ${types.BillingCadence.Year} membership`;
  }

  return `Fi Mini + ${subscriptionProduct.renewalMonths} ${subscriptionProduct.billingCadence} membership`;
}

export function useSubscriptionChooserGroups({
  allowNoSubscription = false,
  subscriptionProducts,
  showReferralPill = false,
  couponPillDisplay,
  showUpsellsInChooser = false,
  monthlyUpgrade = false,
  showSubscriptionLengthDiscount = false,
  prominentPricingDesign = false,
  disableSavingsDisplay = false,
  redemptionType = null,
  hasValidRedemptionCode = false,
  redemptionDiscountInCents = null,
  productCategory,
  onStartFreeTrial,
  freeTrialDisabled = false,
  hideMonthlyOption = false,
}: ChooserGroupsProps) {
  const { t } = useTranslation();
  const isReferralV2 = useIsReferralCheckoutV2();
  // For the subscription chooser, we usually don't want to show any plans that are meant to be only for upsells which will
  // be made available via an interstitial modal, but the in-app cancel flow doesn't employ upselling in the same way,
  // so we set the showUpsellsInChooser and surface all purchasable plans.
  const filteredSubscriptionProducts = useMemo(
    () =>
      showUpsellsInChooser
        ? subscriptionProducts
        : subscriptionProducts.filter((subscriptionProduct) => !subscriptionProduct.upsellOnly),
    [subscriptionProducts, showUpsellsInChooser],
  );

  const locale = useLocale();

  const isGift = useIsGift();

  const activePromoCode = useActivePromoCode();
  const promoCodeDetails = usePromoCodeDetails(showSubscriptionLengthDiscount ? activePromoCode : undefined);
  const promoDiscountsBySku = useMemo(() => {
    if (!promoCodeDetails) return undefined;

    const setupFeeDiscountsByPlatform = new Map();
    promoCodeDetails.setupFees.forEach((fee) => {
      // Server may return multiple setup fees per platform; first match applies to this plan.
      if (!setupFeeDiscountsByPlatform.has(fee.platform)) {
        setupFeeDiscountsByPlatform.set(fee.platform, fee.discountInCents);
      }
    });

    return new Map(
      promoCodeDetails.items.map((item) => {
        const setupFeeDiscount = setupFeeDiscountsByPlatform.get(item.platform) ?? 0;
        return [item.sku, item.discountInCents + setupFeeDiscount];
      }),
    );
  }, [promoCodeDetails]);

  const chooserGroup = useCallback(
    (
      subscriptionProductGroup: SubscriptionOption[],
      groupLabel?: string,
      shouldShowReferralPill = false,
      couponPill?: CouponPillDisplay,
    ): ChooserGroup<string | undefined> => ({
      label: groupLabel,
      options: subscriptionProductGroup.map((subscriptionProduct) => {
        const price = formattedCentsToDollars(getLocalizedPricing(subscriptionProduct.pricingInCents, locale.currency));
        return {
          content: (
            <SubscriptionOptionButton
              overrideTitle={isGift ? price : undefined}
              overrideSubtitle={isGift ? giftOverrideSubtitle(subscriptionProduct) : undefined}
              subscriptionProduct={subscriptionProduct}
              monthlyUpgrade={monthlyUpgrade}
              showSubscriptionLengthDiscount={showSubscriptionLengthDiscount}
              prominentPricingDesign={prominentPricingDesign}
              disableSavingsDisplay={disableSavingsDisplay}
              redemptionType={redemptionType}
              hasValidRedemptionCode={hasValidRedemptionCode}
              redemptionDiscountInCents={redemptionDiscountInCents}
              isReferral={showReferralPill}
              isReferralV2={isReferralV2}
              productCategory={productCategory}
              onStartFreeTrial={
                subscriptionProduct.freeTrial ? () => onStartFreeTrial?.(subscriptionProduct.sku) : undefined
              }
              promoDiscountInCents={promoDiscountsBySku?.get(subscriptionProduct.sku)}
            />
          ),
          value: subscriptionProduct.sku,
        };
      }),
      showReferralPill: shouldShowReferralPill,
      couponPill,
    }),
    [
      disableSavingsDisplay,
      monthlyUpgrade,
      prominentPricingDesign,
      showSubscriptionLengthDiscount,
      redemptionType,
      hasValidRedemptionCode,
      redemptionDiscountInCents,
      locale.currency,
      isGift,
      showReferralPill,
      isReferralV2,
      productCategory,
      onStartFreeTrial,
      promoDiscountsBySku,
    ],
  );

  const yearlyChooserGroup = useMemo(
    () =>
      chooserGroup(
        filteredSubscriptionProducts.filter(
          (subscriptionProduct) =>
            !subscriptionProduct.buyItMembership && subscriptionProduct.billingCadence === types.BillingCadence.Year,
        ),
      ),
    [chooserGroup, filteredSubscriptionProducts],
  );

  // Don't show nano ecomm test variant on in-app collar management screens
  const nanoEcomTestTwoEnabled =
    !window.location.pathname.startsWith('/subscription') &&
    !window.location.pathname.startsWith('/purchasesubscription') &&
    (showNanoVariant() || getFiGrowthBook().getFeatureValue('nano-ecom-test-2', false));

  const hideProductForNanoExperiment =
    showRescuePdpRedesign() ||
    (showNanoVariant() && getFiGrowthBook().getFeatureValue('nano-elevated-promo-offer', false));

  // Check if the user is enrolled in the ecom-s3-plus-price-test experiment
  const isEcomS3PlusPriceTest = getFiGrowthBook().getFeatureValue('ecom-s3-plus-price-test', false);

  const monthToMonthChooserGroup = useMemo(
    () =>
      chooserGroup(
        filteredSubscriptionProducts.filter(
          (subscriptionProduct) =>
            !subscriptionProduct.buyItMembership &&
            subscriptionProduct.billingCadence === types.BillingCadence.Month &&
            !showReferralPill && // Hide monthly plan for referral users
            ((subscriptionProduct.renewalMonths === 1 && !hideProductForNanoExperiment && !hideMonthlyOption) ||
              // Include 3-month plan in month-to-month group when experiment is active
              // Product wants to keep things looking as close to the control group as possible in hopes that
              // this is one less variable that could impact the results of the experiment. The control group
              // has the month to month in separate section below the more expensive prepaid options, so
              // this experiment is just trying to replace the month-to-month option with the 3-month option.
              (isEcomS3PlusPriceTest && subscriptionProduct.renewalMonths === 3)),
        ),
        nanoEcomTestTwoEnabled
          ? isEcomS3PlusPriceTest
            ? t('or_try_a_3_month_plan')
            : t('or_try_a_monthly_plan')
          : undefined,
      ),
    [
      chooserGroup,
      filteredSubscriptionProducts,
      hideProductForNanoExperiment,
      hideMonthlyOption,
      nanoEcomTestTwoEnabled,
      isEcomS3PlusPriceTest,
      showReferralPill,
      t,
    ],
  );

  const isFreeTrialEnabled = useFreeTrialEnabled();

  const hasFreeTrialProducts =
    isFreeTrialEnabled &&
    filteredSubscriptionProducts.some((subscriptionProduct) => subscriptionProduct.freeTrial === true);

  const prepaidChooserGroup = useMemo(
    () =>
      chooserGroup(
        filteredSubscriptionProducts.filter(
          (subscriptionProduct) =>
            !subscriptionProduct.buyItMembership &&
            subscriptionProduct.billingCadence === types.BillingCadence.Month &&
            subscriptionProduct.renewalMonths > 1 &&
            subscriptionProduct.freeTrial !== true &&
            // Exclude 3-month plan from prepaid group when experiment is active
            // Product wants to keep things looking as close to the control group as possible in hopes that
            // this is one less variable that could impact the results of the experiment. The control group
            // has the month to month in separate section below the more expensive prepaid options, so
            // this experiment is just trying to replace the month-to-month option with the 3-month option.
            (isEcomS3PlusPriceTest ? subscriptionProduct.renewalMonths !== 3 : true) &&
            (hideProductForNanoExperiment ? subscriptionProduct.renewalMonths < 24 : true),
        ),
        // Show "Prepay and save" header if there are month-to-month options OR free trial products
        monthToMonthChooserGroup.options.length > 0 || hasFreeTrialProducts ? t('prepay_and_save') : undefined,
        showReferralPill,
        couponPillDisplay,
      ),
    [
      chooserGroup,
      filteredSubscriptionProducts,
      monthToMonthChooserGroup.options.length,
      hasFreeTrialProducts,
      showReferralPill,
      couponPillDisplay,
      hideProductForNanoExperiment,
      isEcomS3PlusPriceTest,
      t,
    ],
  );

  const buyItChooserGroup = useMemo(
    () =>
      chooserGroup(
        filteredSubscriptionProducts.filter((subscriptionProduct) => subscriptionProduct.buyItMembership),
        t('or_pay_one_time'), // Group header
      ),
    [chooserGroup, filteredSubscriptionProducts, t],
  );

  const freeTrialChooserGroup = useMemo(() => {
    // hide the entire free trial group when user is ineligible
    if (freeTrialDisabled) {
      const emptyGroup: Pick<ChooserGroup<string | undefined>, 'options'> = { options: [] };
      return emptyGroup;
    }

    const group = chooserGroup(
      isFreeTrialEnabled
        ? filteredSubscriptionProducts.filter(
            (subscriptionProduct) => !subscriptionProduct.buyItMembership && subscriptionProduct.freeTrial === true,
          )
        : [],
      t('try_fi_for_free'),
    );

    if (isReferralV2 && group.options.length > 0) {
      group.footer = <WhatsIncluded productCategory={productCategory} />;
    }

    return group;
  }, [
    chooserGroup,
    filteredSubscriptionProducts,
    freeTrialDisabled,
    isFreeTrialEnabled,
    isReferralV2,
    productCategory,
    t,
  ]);

  const chooserGroups = useMemo(() => {
    const hasFreeTrial = freeTrialChooserGroup.options.length > 0;

    let groups = [yearlyChooserGroup, monthToMonthChooserGroup, prepaidChooserGroup, buyItChooserGroup];

    // when free trial exists, put it first for better discoverability
    if (hasFreeTrial) {
      groups = [
        freeTrialChooserGroup,
        yearlyChooserGroup,
        prepaidChooserGroup,
        monthToMonthChooserGroup,
        buyItChooserGroup,
      ];
    }

    if (nanoEcomTestTwoEnabled) {
      // nano experiment: simplified options
      groups = hasFreeTrial
        ? [freeTrialChooserGroup, prepaidChooserGroup]
        : [prepaidChooserGroup, monthToMonthChooserGroup];
    }

    if (allowNoSubscription) {
      groups.push({
        options: [
          {
            content: <div className={styles.noSubscriptionOption}>{t('i_dont_need_a_plan')}</div>,
            value: undefined,
          },
        ],
      });
    }
    return groups.filter((group) => group.options.length > 0);
  }, [
    allowNoSubscription,
    buyItChooserGroup,
    freeTrialChooserGroup,
    monthToMonthChooserGroup,
    prepaidChooserGroup,
    yearlyChooserGroup,
    nanoEcomTestTwoEnabled,
    t,
  ]);

  return chooserGroups;
}

const OllieWelcomeBox = () => {
  const { t } = useTranslation();
  return (
    <div className={styles.ollieWelcomeBox}>
      <img className={styles.ollieWelcomeBoxImage} src={OllieWelcomeBoxImage} alt={t('ollie_welcome_box')} />
      <div className={styles.ollieWelcomeBoxContent}>
        <div className={styles.ollieWelcomeBoxTitle}>{t('free_ollie_starter_box')}</div>
        <div className={styles.ollieWelcomeBoxDescription}>
          {t('receive_two_weeks_human_grade_dog_food_welcome_kit')}
        </div>
      </div>
    </div>
  );
};

interface SubscriptionOptionsProps {
  allowNoSubscription?: boolean;
  onSubscriptionChange: (sku: string | undefined) => void;
  selectedSubscriptionSku?: string;
  subscriptionProducts: types.ISubscriptionProduct[];
  title?: string;
  subtitle?: string;
  showReferralPill?: boolean;
  couponPillDisplay?: CouponPillDisplay;
  productName: string;
  productCategory?: types.ProductCategory;
  subscriptionPickerRef?: MutableRefObject<HTMLDivElement | null>;
  startAsUnselected: boolean;
  disableSection: boolean;
  /**
   * Hides the picker while leaving it mounted, so the caller's default plan stays selected and
   * submittable. For surfaces that state the plan in their own copy instead of offering a choice.
   */
  hidden?: boolean;
  prominentPricingDesign?: boolean;
  redemptionType?: types.RedemptionType;
  hasValidRedemptionCode?: boolean;
  redemptionDiscountInCents?: number | null;
  onFreeTrialAddToCart?: (subscriptionSku: string) => void;
  collarProductId?: string;
}

export default function SubscriptionOptions({
  allowNoSubscription = false,
  onSubscriptionChange,
  selectedSubscriptionSku,
  subscriptionProducts,
  title,
  showReferralPill = false,
  subtitle,
  couponPillDisplay,
  productName,
  productCategory,
  subscriptionPickerRef,
  startAsUnselected,
  disableSection,
  hidden = false,
  prominentPricingDesign = false,
  redemptionType = null,
  hasValidRedemptionCode = false,
  redemptionDiscountInCents = null,
  onFreeTrialAddToCart,
  collarProductId,
}: SubscriptionOptionsProps) {
  const { currency } = useLocale();
  const { t } = useTranslation();
  const siteConfig = useSelector((state: types.AppState) => state.config.siteConfig);
  const gwpFreeTrialMonths = siteConfig.gwpFreeTrialMonths;
  const isUpgradePath = window.location.pathname.includes('upgrade');

  const { isEligible: isFreeTrialEligible } = useFreeTrialEligibility(collarProductId ?? '');
  const titleForSubscriptionOptions = redemptionType ? t('claim_your_exclusive_offer') : title || t('choose_your_plan');
  const { country } = useLocale();
  const promoBannerSupportedCountries = getFiGrowthBook().getFeatureValue<string[]>(
    'ecom-promo-banner-supported-countries',
    ['US', 'CA', 'GB'],
  );
  const showExclusiveOfferTag = useIsGrowthBookEnabled()
    ? (getFiGrowthBook().getFeatureValue<boolean>('ecom-subscription-options-offer-tag', false) &&
        !window.location.pathname.includes('fi-mini')) ||
      (getFiGrowthBook().getFeatureValue<boolean>('ecom-subscription-options-offer-tag-mini', false) &&
        window.location.pathname.includes('fi-mini'))
    : false;
  const showSubscriptionLengthDiscount =
    !isUpgradePath && !redemptionType && showExclusiveOfferTag && promoBannerSupportedCountries.includes(country);

  const [showSelected, setShowSelected] = useState<boolean>(!startAsUnselected);

  // Track initial subscription SKU to detect external changes (e.g., from sticky footer)
  const initialSubscriptionSkuRef = useRef(selectedSubscriptionSku);

  // Update visual selection when subscription is selected externally (e.g., from sticky footer)
  // Only trigger when SKU changes from its initial value, not on mount
  useEffect(() => {
    if (selectedSubscriptionSku && !showSelected && selectedSubscriptionSku !== initialSubscriptionSkuRef.current) {
      setShowSelected(true);
    }
  }, [selectedSubscriptionSku, showSelected]);

  // Handler for when user clicks "Start free trial" in the modal - select and add to cart
  const handleStartFreeTrial = useCallback(
    (subscriptionSku: string) => {
      setShowSelected(true);
      onSubscriptionChange(subscriptionSku);

      const subscription = subscriptionProducts.find((product) => product.sku === subscriptionSku);
      if (subscription) {
        events.subscriptionSelected(subscription, currency);
      }

      // Trigger add to cart if handler is provided
      if (onFreeTrialAddToCart) {
        onFreeTrialAddToCart(subscriptionSku);
      }
    },
    [onSubscriptionChange, subscriptionProducts, onFreeTrialAddToCart, currency],
  );

  const chooserGroups = useSubscriptionChooserGroups({
    allowNoSubscription,
    subscriptionProducts,
    showReferralPill,
    couponPillDisplay,
    showSubscriptionLengthDiscount,
    prominentPricingDesign,
    disableSavingsDisplay: !!redemptionType,
    redemptionType,
    hasValidRedemptionCode,
    redemptionDiscountInCents,
    productCategory,
    onStartFreeTrial: handleStartFreeTrial,
    freeTrialDisabled: !isFreeTrialEligible,
    hideMonthlyOption: !monthlySubscriptionIsSupportedInCountry(country),
  });

  useEffect(() => {
    if (subscriptionProducts.length > 0) {
      events.subscriptionPlansDisplayed(subscriptionProducts, productName, currency);
    }
  }, [subscriptionProducts, productName, currency]);

  // Don't show nano ecomm test variant on in-app collar management screens
  const nanoEcomTestTwoEnabled =
    !window.location.pathname.startsWith('/subscription') &&
    !window.location.pathname.startsWith('/purchasesubscription') &&
    (showNanoVariant() || getFiGrowthBook().getFeatureValue('nano-ecom-test-2', false));
  const showOllieWelcomeBox = getFiGrowthBook().getFeatureValue('ollie-welcome-box', false);
  const showNanoValueProps = !window.location.pathname.startsWith('/subscription') && showNanoVariant();

  const needsSelection = false;
  return (
    <div
      className={classNames('subscription-options', styles.variant, {
        [styles.disabledSection]: disableSection,
        [styles.hiddenSection]: hidden,
      })}
      ref={subscriptionPickerRef}
    >
      <div
        className={classNames(styles.variantTitleGroup, {
          [styles.variantTitleGroupTest]: nanoEcomTestTwoEnabled,
        })}
      >
        <div className={classNames(styles.variantTitle, { [styles.variantTitleError]: needsSelection })}>
          {titleForSubscriptionOptions}
          {redemptionType === 'gwp_free_trial' && gwpFreeTrialMonths && (
            <>
              {':'}
              <br />
              <span className={styles.gwpOfferDetails}>
                {t('fi_collar_plus_months_free', { months: gwpFreeTrialMonths })}
              </span>
            </>
          )}
        </div>
        <div className={classNames(styles.variantSubtitle)}>{subtitle}</div>
      </div>
      {!redemptionType && showNanoValueProps && <NanoValueProps />}
      {!redemptionType && showOllieWelcomeBox && <OllieWelcomeBox />}
      <Chooser
        autoHeight
        isDisabled={disableSection}
        onSelect={(subscriptionSku: string | undefined) => {
          setShowSelected(true);
          onSubscriptionChange(subscriptionSku);

          if (!subscriptionSku) {
            return;
          }

          const subscription = subscriptionProducts.find((product) => product.sku === subscriptionSku);
          if (subscription) {
            events.subscriptionSelected(subscription, currency);
          }
        }}
        selectedOption={showSelected ? selectedSubscriptionSku : undefined}
        groups={chooserGroups}
      />
    </div>
  );
}
